import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, CircularProgress, Typography, styled } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import axios, { type AxiosError } from "axios";
import {
  BottomActionArea,
  BottomActionButton,
  CodeInput,
  ExtraWhiteSpace,
  FormBody,
  FormContentContainer,
  FormErrorMessage,
  FormHeader,
  NavigateBeforeButton,
} from "@components";
import { apiGateway } from "@contexts";
import { Routes } from "@utils";

const PIN_LENGTH = 4;

// Queries that show the doctor's signing state: the profile, and the migration
// state behind the home banner that opens this screen.
const DOCTOR_QUERY_KEY = ["doctor"];
const MIGRATION_STATE_QUERY_KEY = ["migrationState"];

const PinContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "8px",
  marginTop: "32px",
});

const PinLabel = styled(Typography)({
  textAlign: "center",
  padding: "16px 16px 8px",
});

const Description = styled(Typography)(({ theme }) => ({
  textAlign: "center",
  color: theme.palette.text.secondary,
}));

const Bold = styled("span")({
  fontWeight: 600,
});

type ErrorName = "invalidPin" | "inProgress" | "mismatch" | "unknown";

const ERROR_MESSAGES: Record<ErrorName, string> = {
  invalidPin:
    "Nuestro proveedor de firma no aceptó ese PIN. Elige otro e inténtalo de nuevo.",
  inProgress:
    "Todavía estamos terminando de configurar tu firma. Inténtalo de nuevo en un minuto.",
  mismatch: "Los PIN no coinciden.",
  unknown: "No pudimos guardar tu nuevo PIN. Inténtalo de nuevo en unos minutos.",
};

// `apiGateway`'s default handler prints the whole error, request body and PIN
// included, and Sentry keeps console output: this one only passes it on.
const rethrowWithoutLogging = (error: AxiosError): never => {
  throw error;
};

const errorNameOf = (error: unknown): string | undefined =>
  axios.isAxiosError<{ error?: { name?: string } }>(error)
    ? error.response?.data?.error?.name
    : undefined;

/**
 * A migrating doctor whose current PIN our signing provider refused chooses a new
 * one here. The home banner opens it. Once the new certificate exists, the doctor
 * signs with this PIN only.
 */
export function NewBpoPinScreen() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [pin, setPin] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [error, setError] = useState<ErrorName | null>(null);

  const leave = (message: string, variant: "success" | "info") => {
    enqueueSnackbar(message, { variant });
    navigate(Routes.SOLICITUDES);
  };

  const { mutate: saveNewPin, isPending } = useMutation({
    mutationFn: (newPin: string) =>
      apiGateway(
        {
          method: "post",
          url: "/enrolment/new-bpo-pin",
          data: { pin: newPin },
        },
        rethrowWithoutLogging,
      ),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: DOCTOR_QUERY_KEY }),
        queryClient.invalidateQueries({ queryKey: MIGRATION_STATE_QUERY_KEY }),
      ]);
      leave("Listo. Desde ahora firmas con tu nuevo PIN.", "success");
    },
    onError: (mutationError: unknown) => {
      const name = errorNameOf(mutationError);
      if (name === "invalidPin") {
        setPin("");
        setConfirmation("");
        setError("invalidPin");
        return;
      }
      if (name === "secondaryCertificateInProgress") {
        setError("inProgress");
        return;
      }
      if (name === "newBpoPinNotAllowed") {
        leave("No tienes un cambio de PIN pendiente.", "info");
        return;
      }
      // BPO kept an earlier certificate and would discard any new PIN: the
      // banner that led here hides, and support takes it from there.
      if (name === "certificateStillValid") {
        void queryClient.invalidateQueries({
          queryKey: MIGRATION_STATE_QUERY_KEY,
        });
        leave(
          "No pudimos actualizar tu firma electrónica. Mientras tanto, sigues firmando como siempre.",
          "info",
        );
        return;
      }
      setError("unknown");
    },
  });

  // A message about the previous attempt no longer applies once the doctor types.
  const handlePinChange = (value: string) => {
    setPin(value);
    setError(null);
  };
  const handleConfirmationChange = (value: string) => {
    setConfirmation(value);
    setError(null);
  };

  const handleSubmit = () => {
    if (pin !== confirmation) {
      setError("mismatch");
      return;
    }
    setError(null);
    saveNewPin(pin);
  };

  const isComplete =
    pin.length === PIN_LENGTH && confirmation.length === PIN_LENGTH;

  return (
    <>
      <FormHeader
        title="Nuevo PIN de firma"
        titleColor="white"
        left={
          <NavigateBeforeButton
            iconColor="white"
            onClick={() => navigate(Routes.SOLICITUDES)}
          />
        }
      />
      <FormContentContainer disableGutters>
        <FormBody>
          <Description>
            Nuestro proveedor de firma electrónica no aceptó tu PIN actual. Elige
            uno nuevo: desde ahora lo usarás cada vez que firmes, en lugar del
            anterior.
          </Description>
          <PinContainer>
            <PinLabel>
              Crea un <Bold>PIN de 4 dígitos</Bold>
            </PinLabel>
            <CodeInput
              code={pin}
              handleCodeChange={handlePinChange}
              numInputs={PIN_LENGTH}
              isValid={error === null}
              type="number"
              secretInputs
            />
            <PinLabel>Repite el PIN</PinLabel>
            <CodeInput
              code={confirmation}
              handleCodeChange={handleConfirmationChange}
              numInputs={PIN_LENGTH}
              isValid={error === null}
              type="number"
              secretInputs
            />
            {error && <FormErrorMessage>{ERROR_MESSAGES[error]}</FormErrorMessage>}
          </PinContainer>

          <ExtraWhiteSpace />

          <BottomActionArea>
            <BottomActionButton
              onClick={handleSubmit}
              disabled={!isComplete || isPending}
            >
              {isPending ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Guardar nuevo PIN"
              )}
            </BottomActionButton>
          </BottomActionArea>
        </FormBody>
      </FormContentContainer>
    </>
  );
}
